import React, { useEffect, useMemo, useRef, useState } from "react";
import { api } from "@/lib/api";
import { toast } from "sonner";
import { X, Save, Cog, PackageOpen, Clock } from "lucide-react";
import { fmtQty } from "@/lib/pack";

const ago = (iso) => {
  if (!iso) return null;
  const d = Math.floor((Date.now() - new Date(iso).getTime()) / 86400000);
  return d <= 0 ? "today" : d === 1 ? "yesterday" : `${d} days ago`;
};

/**
 * Prompted after a Take Stock action for an item linked to machines.
 * v209 — machines without a counter get an ESTIMATED reading (from their
 * run schedule) pre-filled; the take is always logged, even on Skip.
 */
const MachineHoursPromptDialog = ({ open, stockItem, takeId, qtyTaken, unitCost, onClose }) => {
  const [machines, setMachines] = useState([]);
  const [rows, setRows] = useState({}); // { [mid]: { used, reading, touched, record, cost } }
  const [busy, setBusy] = useState(false);
  const done = useRef(false);

  const defaultCost = useMemo(() => {
    const c = Number(unitCost || 0) * Number(qtyTaken || 0);
    return Number.isFinite(c) && c > 0 ? c.toFixed(2) : "";
  }, [unitCost, qtyTaken]);

  const spendKind = useMemo(() => {
    const c = (stockItem?.category || "").toLowerCase();
    return c === "consumable" || c === "consumables" ? "Consumables" : "Spare parts";
  }, [stockItem?.category]);

  useEffect(() => {
    if (!open || !stockItem?.id) return;
    done.current = false;
    api
      .get(`/machines/hours-estimate/${stockItem.id}`)
      .then(({ data }) => {
        const list = data || [];
        const single = list.length === 1;
        const seed = {};
        list.forEach((m) => {
          seed[m.id] = {
            used: single,
            reading: m.has_hour_meter ? "" : String(m.hours_estimated_now ?? ""),
            touched: false,
            record: single && defaultCost !== "",
            cost: defaultCost,
          };
        });
        setMachines(list);
        setRows(seed);
      })
      .catch(() => setMachines([]));
  }, [open, stockItem?.id, defaultCost]);

  if (!open || !stockItem) return null;
  if (machines.length === 0) return null;

  const setRow = (mid, patch) => setRows((s) => ({ ...s, [mid]: { ...s[mid], ...patch } }));

  const takeLogs = (withReadings) =>
    machines
      .filter((m) => rows[m.id]?.used)
      .map((m) => {
        const r = rows[m.id];
        const sendReading =
          withReadings && r.reading !== "" && !isNaN(Number(r.reading)) && (m.has_hour_meter || r.touched);
        return api.post(`/machines/${m.id}/take-log`, {
          stock_id: stockItem.id,
          stock_name: stockItem.name,
          movement_id: takeId || null,
          qty: qtyTaken ?? null,
          hours: sendReading ? Number(r.reading) : undefined,
        });
      });

  const skip = async () => {
    if (done.current) return onClose?.();
    done.current = true;
    onClose?.();
    try {
      await Promise.all(takeLogs(false));
    } catch (_) {
      /* non-blocking */
    }
  };

  const submit = async () => {
    setBusy(true);
    try {
      const spend = machines.filter((m) => {
        const r = rows[m.id];
        return r?.record && r.cost !== "" && !isNaN(Number(r.cost));
      });
      const logs = takeLogs(true);
      await Promise.all([
        ...logs,
        ...spend.map((m) =>
          api.post(`/machines/${m.id}/services`, {
            kind: spendKind,
            cost: Number(rows[m.id].cost),
            notes: `Auto from stock take: ${stockItem.name}${qtyTaken ? ` × ${fmtQty(qtyTaken)}` : ""}`,
          }),
        ),
      ]);
      done.current = true;
      const bits = [];
      if (logs.length) bits.push(`hours × ${logs.length}`);
      if (spend.length) bits.push(`spend × ${spend.length}`);
      if (bits.length) toast.success(`Logged ${bits.join(" · ")}`);
      onClose?.();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Could not log");
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40" data-testid="machine-hours-prompt" onClick={skip}>
      <div className="nb-card w-[min(620px,94vw)] max-h-[88vh] overflow-y-auto" onClick={(e) => e.stopPropagation()}>
        <div className="sticky top-0 z-10 bg-white px-5 py-4 border-b-2 border-black flex items-center justify-between">
          <div>
            <div className="label-eyebrow">Machine hours & spend</div>
            <h3 className="font-display text-2xl flex items-center gap-2">
              <Cog size={20} strokeWidth={2.5} /> Log after {stockItem.name}
            </h3>
          </div>
          <button data-testid="machine-hours-prompt-close" onClick={skip} className="nb-btn bg-white p-2">
            <X size={16} strokeWidth={2.5} />
          </button>
        </div>

        <div className="p-5 flex flex-col gap-4">
          <div className="text-sm text-[#607087]">
            You just took{" "}
            <span className="font-bold">
              {qtyTaken ? `${fmtQty(qtyTaken)} × ` : ""}
              {stockItem.name}
            </span>
            . {machines.length > 1 ? "Tick the machine(s) it was used on. " : ""}Hours are estimated from each
            machine's run schedule — just Save, or correct it if you know better.
          </div>
          {machines.map((m) => (
            <MachineRow key={m.id} m={m} r={rows[m.id]} spendKind={spendKind} setRow={(p) => setRow(m.id, p)} />
          ))}
        </div>

        <div className="sticky bottom-0 bg-white px-5 py-4 border-t-2 border-black flex justify-end gap-2">
          <button data-testid="machine-hours-skip" onClick={skip} className="nb-btn bg-white px-4 py-2">
            Skip
          </button>
          <button data-testid="machine-hours-save" onClick={submit} disabled={busy} className="nb-btn nb-btn-primary px-4 py-2 flex items-center gap-1">
            <Save size={14} strokeWidth={2.5} /> Save
          </button>
        </div>
      </div>
    </div>
  );
};

const MachineRow = ({ m, r, spendKind, setRow }) => {
  if (!r) return null;
  return (
    <div data-testid={`machine-hours-row-${m.id}`} className={`border-2 border-black rounded-xl p-3 bg-white flex flex-col gap-2 ${r.used ? "" : "opacity-60"}`}>
      <div className="flex items-center gap-3">
        <label className="flex items-center gap-2 flex-1 min-w-0 cursor-pointer">
          <input type="checkbox" data-testid={`machine-used-toggle-${m.id}`} checked={!!r.used} onChange={(e) => setRow({ used: e.target.checked })} className="w-4 h-4" />
          <div className="min-w-0">
            <div className="font-display truncate">{m.name}</div>
            <div className="label-eyebrow text-[#607087]">
              {m.category || "—"}
              {m.hours != null && (
                <>
                  {" · "}Last logged: <span className="font-mono-num">{m.hours}</span>
                </>
              )}
            </div>
          </div>
        </label>
        <div className="w-32 shrink-0 text-right">
          <input
            data-testid={`machine-hours-input-${m.id}`}
            type="number"
            min="0"
            step="any"
            inputMode="decimal"
            disabled={!r.used}
            className="nb-input font-mono-num text-right disabled:opacity-40"
            value={r.reading}
            onChange={(e) => setRow({ reading: e.target.value, touched: true })}
            placeholder={m.has_hour_meter ? "counter hrs" : "hrs"}
          />
          <span
            data-testid={`machine-hours-mode-${m.id}`}
            className={`inline-block mt-1 text-[10px] font-bold px-1.5 py-0.5 rounded ${
              m.has_hour_meter ? "bg-[#0A3A7A] text-white" : r.touched ? "bg-[#FFF7DB] text-[#5B4500]" : "bg-[#E6F4EA] text-[#1F6F3E]"
            }`}
          >
            {m.has_hour_meter ? "READ COUNTER" : r.touched ? "EDITED" : "ESTIMATED"}
          </span>
        </div>
      </div>
      <div data-testid={`machine-last-take-${m.id}`} className="text-xs text-[#607087] flex items-center gap-1">
        <Clock size={12} strokeWidth={2.5} />
        {m.last_take_at
          ? `Last taken for this machine ${ago(m.last_take_at)} · ~${m.hours_since_last_take} working hrs`
          : "First time logged for this machine"}
      </div>
      <div className="flex items-center gap-2 pt-2 border-t border-black/10">
        <label className="flex items-center gap-2 flex-1 min-w-0 cursor-pointer text-sm" data-testid={`machine-spare-toggle-${m.id}`}>
          <input type="checkbox" checked={!!r.record} onChange={(e) => setRow({ record: e.target.checked })} className="w-4 h-4" />
          <PackageOpen size={14} strokeWidth={2.5} className={spendKind === "Consumables" ? "text-[#1F6F3E]" : "text-[#0A3A7A]"} />
          <span className="truncate">
            Record as <b>{spendKind === "Consumables" ? "Consumable" : "Spare part"}</b> spend
          </span>
        </label>
        <div className="flex items-center gap-1 shrink-0">
          <span className="text-[#607087] text-sm">$</span>
          <div className="w-24">
            <input
              data-testid={`machine-spare-cost-${m.id}`}
              type="number"
              step="0.01"
              min="0"
              inputMode="decimal"
              disabled={!r.record}
              className="nb-input font-mono-num text-right disabled:opacity-40"
              value={r.cost}
              onChange={(e) => setRow({ cost: e.target.value })}
              placeholder="0.00"
            />
          </div>
        </div>
      </div>
    </div>
  );
};

export default MachineHoursPromptDialog;
